<template>
  <div class="bg-gray-900 rounded-lg">
    <div class="space-y-1 px-6 py-6 border-b border-gray-800 flex items-center">
      <div class="flex flex-col gap-1 flex-1">
        <h3 class="text-lg leading-6 font-medium text-text-primary">
          {{ $t('shared.coach_settings.title') }}
        </h3>
        <p class="max-w-2xl text-sm text-text-secondary">
          {{ $t('shared.coach_settings.description') }}
        </p>
      </div>
    </div>

    <div class="px-6">
      <dl class="divide-y divide-gray-800">
        <!-- Blocked Mods Section -->
        <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
          <dt class="text-sm font-medium text-text-primary">
            {{ $t('shared.coach_settings.blocked_mods') }}
            <p class="max-w-2xl text-xs text-text-secondary font-normal">
              {{ $t('shared.coach_settings.blocked_mods_description') }}
            </p>
          </dt>

          <dd class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center">
            <!-- Loading state -->
            <div v-if="loading" class="flex items-center justify-center">
              <svg class="animate-spin h-4 w-4 text-text-secondary" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>

            <!-- Mods Dropdown -->
            <div v-else-if="availableMods.length > 0" class="relative">
              <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
                <MenuButton
                  class="relative w-[300px] disabled:bg-gray-800 disabled:text-text-disabled inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
                >
                  <div class="flex-1 text-left">
                    {{ $t('shared.coach_settings.blocked_count', { count: blockedMods.length }) }}
                  </div>
                  <ChevronDownIcon
                    class="-mr-1 ml-4 h-5 w-5"
                    aria-hidden="true"
                  />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute right-0 z-50 mt-2 w-[300px] origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-80 overflow-y-auto"
                  >
                    <div class="flex flex-col py-4 gap-4">
                      <h1 class="text-text-primary px-4">{{ $t('shared.coach_settings.select_blocked_mods') }}</h1>
                      <div class="px-4">
                        <input
                          type="text"
                          v-model="modsSearchQuery"
                          class="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-sm placeholder:text-text-secondary placeholder:font-light placeholder:text-sm text-text-primary"
                          :placeholder="$t('shared.coach_settings.search_mods')"
                        />
                      </div>

                      <div class="flex flex-col">
                        <div
                          v-for="mod in filteredMods"
                          :key="mod.id"
                          class="group flex items-center py-2 text-sm w-full justify-start text-text-primary px-4 cursor-pointer"
                          @click.stop="toggleMod(mod.id)"
                        >
                          <span class="flex-1">{{ mod.friendly_name }}</span>
                          <input
                            type="checkbox"
                            :checked="blockedMods.some(blockedMod => blockedMod.id === mod.id)"
                            class="h-4 w-4 text-primary-500 bg-gray-700 border-gray-600 rounded focus:ring-primary-500 focus:ring-2 ml-2"
                            @click.prevent.stop="toggleMod(mod.id)"
                            @change.prevent.stop
                          />
                        </div>
                      </div>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
            <span v-else class="text-text-secondary text-sm">
              {{ $t('shared.coach_settings.no_mods') }}
            </span>
          </dd>
        </div>

        <!-- Blocked Presets Section -->
        <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
          <dt class="text-sm font-medium text-text-primary">
            {{ $t('shared.coach_settings.blocked_presets') }}
            <p class="max-w-2xl text-xs text-text-secondary font-normal">
              {{ $t('shared.coach_settings.blocked_presets_description') }}
            </p>
          </dt>

          <dd class="mt-1 flex text-sm text-text-primary sm:mt-0 sm:col-span-2 items-center">
            <!-- Presets Dropdown -->
            <div v-if="availablePresets.length > 0" class="relative">
              <Menu as="div" class="relative inline-block text-left w-full lg:w-auto">
                <MenuButton
                  class="relative w-[300px] disabled:bg-gray-800 disabled:text-text-disabled inline-flex h-8 items-center px-2 py-2 border border-gray-700 shadow-sm text-sm font-medium rounded-md text-text-primary bg-gray-800 hover:bg-gray-700"
                >
                  <div class="flex-1 text-left">
                    {{ $t('shared.coach_settings.blocked_count', { count: blockedPresets.length }) }}
                  </div>
                  <ChevronDownIcon
                    class="-mr-1 ml-4 h-5 w-5"
                    aria-hidden="true"
                  />
                </MenuButton>

                <transition
                  enter-active-class="transition ease-out duration-100"
                  enter-from-class="transform opacity-0 scale-95"
                  enter-to-class="transform opacity-100 scale-100"
                  leave-active-class="transition ease-in duration-75"
                  leave-from-class="transform opacity-100 scale-100"
                  leave-to-class="transform opacity-0 scale-95"
                >
                  <MenuItems
                    class="absolute right-0 z-50 mt-2 w-[300px] origin-top-right divide-y divide-gray-100 rounded-md bg-gray-800 border border-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none max-h-80 overflow-y-auto"
                  >
                    <div class="flex flex-col py-4 gap-4">
                      <h1 class="text-text-primary px-4">{{ $t('shared.coach_settings.select_blocked_presets') }}</h1>
                      <div class="px-4">
                        <input
                          type="text"
                          v-model="presetsSearchQuery"
                          class="w-full bg-gray-800 border border-gray-700 rounded-md p-2 text-sm placeholder:text-text-secondary placeholder:font-light placeholder:text-sm text-text-primary"
                          :placeholder="$t('shared.coach_settings.search_presets')"
                        />
                      </div>

                      <div class="flex flex-col">
                        <div
                          v-for="preset in filteredPresets"
                          :key="preset.id"
                          class="group flex items-center py-2 text-sm w-full justify-start text-text-primary px-4 cursor-pointer"
                          @click.stop="togglePreset(preset.id)"
                        >
                          <span class="flex-1">{{ preset.friendly_name }}</span>
                          <input
                            type="checkbox"
                            :checked="blockedPresets.some(blockedPreset => blockedPreset.id === preset.id)"
                            class="h-4 w-4 text-primary-500 bg-gray-700 border-gray-600 rounded focus:ring-primary-500 focus:ring-2 ml-2"
                            @click.prevent.stop="togglePreset(preset.id)"
                            @change.prevent.stop
                          />
                        </div>
                      </div>
                    </div>
                  </MenuItems>
                </transition>
              </Menu>
            </div>
            <span v-else class="text-text-secondary text-sm">
              {{ $t('shared.coach_settings.no_presets') }}
            </span>
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import {
  Menu, MenuButton, MenuItems, MenuItem,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import axios from 'axios';

const store = useStore();
const { t } = useI18n();

// Props
const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
});

// Emit events to parent
const emit = defineEmits(['settingsUpdated']);

// Reactive data
const loading = ref(true);
const saving = ref(false);
const saveSuccess = ref(false);
const availableMods = ref([]);
const availablePresets = ref([]);
const blockedMods = ref([]);
const blockedPresets = ref([]);
const modsSearchQuery = ref('');
const presetsSearchQuery = ref('');

// Computed
const modInfo = computed(() => store.getters.modInfo);

const filteredMods = computed(() => {
  if (!modsSearchQuery.value) {
    return availableMods.value;
  }

  const query = modsSearchQuery.value.toLowerCase();
  return availableMods.value.filter((mod) => mod.friendly_name.toLowerCase().includes(query)
    || mod.name.toLowerCase().includes(query));
});

const filteredPresets = computed(() => {
  if (!presetsSearchQuery.value) {
    return availablePresets.value;
  }

  const query = presetsSearchQuery.value.toLowerCase();
  return availablePresets.value.filter((preset) => preset.friendly_name.toLowerCase().includes(query));
});

function showValidationError(message) {
  store.commit('showMessage', {
    messageType: 'error',
    messageText: message,
  });

  setTimeout(() => {
    store.commit('hideMessage');
  }, 3000);
}

async function toggleMod(modId) {
  const isCurrentlyBlocked = blockedMods.value.some((blockedMod) => blockedMod.id === modId);

  if (!isCurrentlyBlocked && blockedMods.value.length >= availableMods.value.length - 1) {
    showValidationError(t('shared.coach_settings.min_mods_required'));
    return;
  }

  try {
    const response = await axios.post(`/users/toggle_coach_mod_block/${modId}`);

    if (response.status === 200) {
      const mod = availableMods.value.find((m) => m.id === modId);

      if (isCurrentlyBlocked) {
        // Remove from blocked list
        blockedMods.value = blockedMods.value.filter((blockedMod) => blockedMod.id !== modId);
      } else {
        // Add to blocked list
        if (mod) {
          blockedMods.value.push(mod);
        }
      }
    }
  } catch (error) {
    console.error('Error toggling mod:', error);
    store.commit('showMessage', {
      messageType: 'error',
      messageText: error.response?.data?.error || t('shared.coach_settings.toggle_mod_failed'),
    });

    setTimeout(() => {
      store.commit('hideMessage');
    }, 3000);
  }
}

async function togglePreset(presetId) {
  const isCurrentlyBlocked = blockedPresets.value.some((blockedPreset) => blockedPreset.id === presetId);

  if (!isCurrentlyBlocked && blockedPresets.value.length >= availablePresets.value.length - 1) {
    showValidationError(t('shared.coach_settings.min_presets_required'));
    return;
  }

  try {
    const response = await axios.post(`/users/toggle_coach_preset_block/${presetId}`);

    if (response.status === 200) {
      const preset = availablePresets.value.find((p) => p.id === presetId);

      if (isCurrentlyBlocked) {
        // Remove from blocked list
        blockedPresets.value = blockedPresets.value.filter((blockedPreset) => blockedPreset.id !== presetId);
      } else {
        // Add to blocked list
        if (preset) {
          blockedPresets.value.push(preset);
        }
      }
    }
  } catch (error) {
    console.error('Error toggling preset:', error);
    store.commit('showMessage', {
      messageType: 'error',
      messageText: error.response?.data?.error || t('shared.coach_settings.toggle_preset_failed'),
    });

    setTimeout(() => {
      store.commit('hideMessage');
    }, 3000);
  }
}

async function loadAvailableOptions() {
  try {
    loading.value = true;

    // Load mods from store (modInfo should already be loaded)
    if (modInfo.value?.mods) {
      availableMods.value = modInfo.value.mods.filter((mod) => mod.taskable && mod.cs2);
    }

    // Load presets from API
    const presetsResponse = await axios.get('/presets');
    availablePresets.value = presetsResponse.data || [];

    // Load blocked mods and presets
    await loadBlockedItems();
  } catch (error) {
    console.error('Error loading coach settings options:', error);
    store.commit('showMessage', {
      messageType: 'error',
      messageText: t('shared.coach_settings.load_failed'),
    });
  } finally {
    loading.value = false;
  }
}

async function loadBlockedItems() {
  try {
    const [blockedModsResponse, blockedPresetsResponse] = await Promise.all([
      axios.get('/users/blocked_coach_mods'),
      axios.get('/users/blocked_coach_presets'),
    ]);

    blockedMods.value = blockedModsResponse.data || [];
    blockedPresets.value = blockedPresetsResponse.data || [];
  } catch (error) {
    console.error('Error loading blocked items:', error);
    blockedMods.value = [];
    blockedPresets.value = [];
  }
}

// Lifecycle
onMounted(async () => {
  await loadAvailableOptions();
});
</script>
